import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { Tooltip } from '#src/atoms/tooltip/Tooltip/index.tsx';
import {
  GameplayTierStars,
  type GameplayTierStarsVariant,
} from '#src/components/card/GameplayTierStars/index.tsx';
import { MonetaryRewardDisclaimer } from '#src/components/jonum/MonetaryRewardDisclaimer/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useMonetaryAmount } from '#src/hooks/useMonetaryAmount.ts';
import { range } from '#src/lib/arrays.ts';
import { isType, isTypes } from '#src/lib/gql/index.ts';
import { getMonetaryAmountDisplayCurrency } from '#src/lib/monetaryAmount.ts';
import { qualityNames } from '#src/lib/players.ts';

import { BoxStar } from './BoxStar/index.tsx';
import type { RewardConfigText_anyRewardConfigInterface } from './__generated__/index.graphql.ts';

const Stars = styled(Horizontal).attrs({ as: 'span' })`
  gap: 2px;
`;

type Props = {
  anyRewardConfig: RewardConfigText_anyRewardConfigInterface;
  starsVariant?: GameplayTierStarsVariant;
  numberNotation?: 'standard' | 'compact';
  disableMonetaryRewardDisclaimer?: boolean;
};

export const RewardConfigText = ({
  anyRewardConfig,
  starsVariant = 'default',
  numberNotation = 'standard',
  disableMonetaryRewardDisclaimer,
}: Props) => {
  const { formatNumber } = useIntl();
  const { toMonetaryAmount } = useMonetaryAmount();
  const { fiatCurrency } = useCurrentUserContext();

  // Support new CardRewardConfig
  if (
    isType(anyRewardConfig, 'CardRewardConfig') &&
    anyRewardConfig.qualities?.length
  ) {
    return qualityNames[anyRewardConfig.qualities[0]];
  }

  if (isType(anyRewardConfig, 'CardRewardConfig')) {
    const gameplayTier =
      anyRewardConfig.gameplayTier || anyRewardConfig.u23GameplayTier;

    if (anyRewardConfig.quality) {
      return qualityNames[anyRewardConfig.quality];
    }
    if (gameplayTier) {
      return (
        <GameplayTierStars
          gameplayTier={gameplayTier}
          variant={starsVariant}
          size={starsVariant === 'flat' ? 1.5 : 1}
        />
      );
    }
  }

  if (
    isType(anyRewardConfig, 'ProbabilisticBundleRewardConfig') &&
    anyRewardConfig.probabilisticBundleConfig.stars
  ) {
    return (
      <Stars>
        {range(5).map(i => (
          <BoxStar
            key={i}
            rarity={anyRewardConfig.probabilisticBundleConfig.rarity}
            lightened={
              i < (anyRewardConfig.probabilisticBundleConfig.stars ?? 0)
            }
            size={1.5}
          />
        ))}
      </Stars>
    );
  }

  if (
    isTypes(anyRewardConfig, [
      'MonetaryRewardConfig',
      'ConversionCreditRewardConfig',
    ])
  ) {
    const amount = isType(anyRewardConfig, 'MonetaryRewardConfig')
      ? anyRewardConfig.monetaryAmount
      : anyRewardConfig.maxDiscount;

    const displayCurrency = getMonetaryAmountDisplayCurrency(
      amount,
      fiatCurrency
    );
    const displayAmount = toMonetaryAmount(amount).toMajorUnit(displayCurrency);

    const amountNode = (
      // oxlint-disable-next-line sorare/enforce-monetary-reward-disclaimer -- wrapped below unless the caller opts out (e.g. a struck-through original amount next to an already-disclaimed boosted one)
      <FormattedNumber
        value={displayAmount}
        style="currency"
        currency={displayCurrency}
        currencyDisplay="narrowSymbol"
        maximumFractionDigits={0}
        notation={displayAmount > 10000 ? 'compact' : 'standard'}
      />
    );
    if (disableMonetaryRewardDisclaimer) {
      return amountNode;
    }
    if (isType(anyRewardConfig, 'MonetaryRewardConfig')) {
      return (
        <MonetaryRewardDisclaimer variant="monetary">
          {amountNode}
        </MonetaryRewardDisclaimer>
      );
    }
    return (
      <Tooltip
        title={
          <FormattedMessage
            defaultMessage="This market credit provides a {percent} discount up to {amount}."
            values={{
              percent: (
                <FormattedNumber
                  value={anyRewardConfig.percentageDiscount}
                  style="percent"
                />
              ),
              amount: amountNode,
            }}
          />
        }
      >
        {amountNode}*
      </Tooltip>
    );
  }

  let count = 1;
  if (isType(anyRewardConfig, 'CardShardRewardConfig')) {
    count = anyRewardConfig.quantity;
  } else if (
    isTypes(anyRewardConfig, ['CoinRewardConfig', 'InGameCurrencyRewardConfig'])
  ) {
    count = anyRewardConfig.amount;
  }

  const formattedCount = formatNumber(count, {
    maximumFractionDigits: 1,
    notation: numberNotation ?? (count > 10000 ? 'compact' : 'standard'),
    compactDisplay: 'short',
  });

  return formattedCount;
};

RewardConfigText.fragments = {
  anyRewardConfigInterface: gql`
    fragment RewardConfigText_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ... on CardRewardConfig {
        id
        gameplayTier
        u23GameplayTier
        qualities
        quality: qualityTyped
      }
      ... on CardShardRewardConfig {
        id
        quantity
      }
      ... on CoinRewardConfig {
        id
        amount
      }
      ... on InGameCurrencyRewardConfig {
        id
        amount
      }
      ... on ConversionCreditRewardConfig {
        id
        percentageDiscount
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ... on MonetaryRewardConfig {
        id
        monetaryAmount: amount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ... on ProbabilisticBundleRewardConfig {
        id
        probabilisticBundleConfig {
          id
          rarity
          stars
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<RewardConfigText_anyRewardConfigInterface>,
};
